import React, { useState, useMemo, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaCrown, FaCalendarAlt, FaCreditCard, FaExchangeAlt, FaTrash, FaSpinner, FaCheck } from 'react-icons/fa';
import { useSubscription } from '../../hooks/useSubscription';
import { useToast } from '../../hooks/useToast';
import { useAuth } from '../../hooks/useAuth';
import Modal from '../common/Modal';
import subscriptionConfig from '../../config/subscription';

/**
 * Component to display detailed subscription information and management options
 */
const SubscriptionDetails = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { 
    subscription, 
    loading, 
    getCurrentTier, 
    isSubscriptionActive, 
    getDaysRemaining,
    cancelSubscription,
    changeSubscription,
    fetchSubscription
  } = useSubscription();
  const { user } = useAuth();
  
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [showChangeModal, setShowChangeModal] = useState(false);
  const [changingPlan, setChangingPlan] = useState(false);
  const [selectedTier, setSelectedTier] = useState('');
  const [billingCycle, setBillingCycle] = useState('');
  const [changeSuccess, setChangeSuccess] = useState(false);
  const [changeError, setChangeError] = useState('');
  const [needsCheckout, setNeedsCheckout] = useState(false);
  
  // Get subscription plans based on user type
  const subscriptionPlans = useMemo(() => {
    return user?.role === 'school_admin' 
      ? subscriptionConfig.schoolSubscriptions 
      : subscriptionConfig.parentSubscriptions;
  }, [user]);

  // Get tier configuration based on user type
  const tierConfig = useMemo(() => {
    if (!subscription) return null;
    
    const currentTier = getCurrentTier() || 
      (user?.role === 'school_admin' ? 'ignite' : 'free');
    
    return subscriptionPlans[currentTier] || null;
  }, [subscription, getCurrentTier, user, subscriptionPlans]);
  
  // Reset state when modal closes
  useEffect(() => {
    if (!showChangeModal) {
      setChangeSuccess(false);
      setChangeError('');
      setNeedsCheckout(false);
    }
  }, [showChangeModal]);

  const featuresLimit = 5; // Number of features to show initially
  
  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };
  
  // Handle subscription cancellation confirmation
  const handleCancelConfirm = async () => {
    setCancelling(true);
    try {
      const result = await cancelSubscription();
      if (result) {
        setShowCancelModal(false);
        showToast('Your subscription has been successfully cancelled', 'success');
        // Refresh subscription data
        await fetchSubscription();
      } else {
        showToast('Failed to cancel subscription. Please try again or contact support.', 'error');
      }
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      showToast('An unexpected error occurred. Please try again later.', 'error');
    } finally {
      setCancelling(false);
    }
  };
  
  // Handle subscription tier change confirmation
  const handleChangeTier = async () => {
    if (!selectedTier || !billingCycle) {
      setChangeError('Please select a plan and billing cycle');
      return;
    }

    // Don't proceed if selected tier is the current tier with same billing cycle
    if (selectedTier === getCurrentTier() && billingCycle === subscription.billingCycle) {
      setChangeError('This is already your current plan. Please select a different plan or billing cycle.');
      return;
    }
    
    setChangingPlan(true);
    setChangeError('');
    setNeedsCheckout(false);
    
    try {
      const result = await changeSubscription(selectedTier, billingCycle);
      
      if (result.success) {
        setChangeSuccess(true);
        
        if (result.needsCheckout) {
          setNeedsCheckout(true);
          setTimeout(() => {
            setShowChangeModal(false);
            navigate('/checkout', { 
              state: { 
                selectedPlan: selectedTier,
                billingCycle 
              }
            });
          }, 1500);
        } else {
          setTimeout(() => {
            setShowChangeModal(false);
            fetchSubscription()
              .then(() => {
                showToast('Subscription updated successfully', 'success');
              })
              .catch(err => {
                console.error('Error fetching updated subscription:', err);
              });
          }, 1500);
        }
      } else {
        setChangeError(result.message || 'Failed to change subscription plan. Please try again.');
      }
    } catch (error) {
      console.error('Error changing subscription:', error);
      setChangeError('An unexpected error occurred. Please try again later.');
    } finally {
      setChangingPlan(false);
    }
  };
  
  // Handle opening change plan modal with pre-selected values
  const openChangePlanModal = () => {
    if (subscription) {
      setSelectedTier(getCurrentTier() || '');
      setBillingCycle(subscription.billingCycle || 'annual');
    } else {
      setBillingCycle('annual');
    }
    setShowChangeModal(true);
  };
  
  if (loading) {
    return (
      <div className="flex justify-center items-center h-48">
        <FaSpinner className="animate-spin text-3xl text-primary" />
      </div>
    );
  }
  
  if (!subscription) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="text-center py-8">
          <FaCrown className="text-5xl text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold mb-2">No Active Subscription</h3>
          <p className="text-gray-600 mb-6">You don't have an active subscription yet.</p>
          <Link
            to="/subscriptions"
            className="px-6 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors"
          >
            View Subscription Plans
          </Link>
        </div>
      </div>
    );
  }
  
  // Use defaults if tierConfig is missing
  const planName = tierConfig?.name || subscription.tier || (user?.role === 'school_admin' ? 'School' : 'Basic');
  const planCycle = subscription.billingCycle === 'annual' ? 'Annual' : 'Quarterly';
  const planPrice = tierConfig?.price 
    ? (subscription.billingCycle === 'annual' 
        ? tierConfig.price.annual 
        : tierConfig.price.quarterly) 
    : (user?.role === 'school_admin' ? 'Contact for pricing' : 'Custom price');
  
  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      {/* Subscription header with plan name */}
      <div className="bg-primary text-white p-6 flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center">
            <FaCrown className="mr-2" /> {planName} Plan
          </h2>
          <p className="mt-1 text-white text-opacity-90">
            {isSubscriptionActive() 
              ? `${getDaysRemaining()} days remaining` 
              : 'Subscription expired'}
          </p>
        </div>
        <div className="text-right">
          <span className="block text-lg font-bold">
            {planCycle}
          </span>
          <span className="text-white text-opacity-80">
            {typeof planPrice === 'number'
              ? `KES ${planPrice.toLocaleString()}`
              : planPrice}
          </span>
        </div>
      </div>
      
      {/* Subscription details */}
      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Left column - Subscription info */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Subscription Details</h3>
            
            <div className="space-y-4">
              <div className="flex items-center">
                <FaCalendarAlt className="text-gray-500 mr-3" />
                <div>
                  <div className="text-sm text-gray-500">Start Date</div>
                  <div>{formatDate(subscription.startDate)}</div>
                </div>
              </div>
              
              <div className="flex items-center">
                <FaCalendarAlt className="text-gray-500 mr-3" />
                <div>
                  <div className="text-sm text-gray-500">End Date</div>
                  <div>{formatDate(subscription.endDate)}</div>
                </div>
              </div>
              
              <div className="flex items-center">
                <FaCreditCard className="text-gray-500 mr-3" />
                <div>
                  <div className="text-sm text-gray-500">Payment Method</div>
                  <div className="capitalize">{subscription.paymentMethod || 'Not specified'}</div>
                </div>
              </div>
              
              {subscription.paymentDetails && subscription.paymentDetails.cardLast4 && (
                <div className="flex items-center">
                  <div className="w-6 mr-3"></div> {/* Spacer for alignment */}
                  <div>
                    <div className="text-sm text-gray-500">Card</div>
                    <div className="capitalize">
                      {subscription.paymentDetails.cardType || 'Card'} ending in {subscription.paymentDetails.cardLast4}
                    </div>
                  </div>
                </div>
              )}
              
              <div className="flex items-center">
                <div className="w-6 mr-3"></div> {/* Spacer for alignment */}
                <div>
                  <div className="text-sm text-gray-500">Status</div>
                  <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    isSubscriptionActive()
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {isSubscriptionActive() ? 'Active' : 'Inactive'}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Action buttons */}
            <div className="mt-6 space-y-3">
              <button
                onClick={openChangePlanModal}
                className="w-full flex items-center justify-center px-4 py-2 border border-primary text-primary rounded-md hover:bg-primary hover:text-white transition-colors"
                disabled={loading}
              >
                <FaExchangeAlt className="mr-2" /> Change Plan
              </button>
              
              {isSubscriptionActive() && (
                <button
                  onClick={() => setShowCancelModal(true)}
                  className="w-full flex items-center justify-center px-4 py-2 border border-red-500 text-red-500 rounded-md hover:bg-red-500 hover:text-white transition-colors"
                  disabled={loading}
                >
                  <FaTrash className="mr-2" /> Cancel Subscription
                </button>
              )}
            </div>
          </div>
          
          {/* Right column - Plan features */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Plan Features</h3>
            
            {tierConfig && tierConfig.features && (
              <ul className="space-y-2">
                {tierConfig.features.slice(0, featuresLimit).map((feature, index) => (
                  <li key={index} className="flex items-start">
                    <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-green-100 text-green-800 mr-2">
                      <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </span>
                    {feature}
                  </li>
                ))}
                
                {tierConfig.features && tierConfig.features.length > featuresLimit && (
                  <li className="text-primary text-sm mt-2 cursor-pointer hover:underline">
                    + {tierConfig.features.length - featuresLimit} more features
                  </li>
                )}
              </ul>
            )}
            
            {(!tierConfig || !tierConfig.features) && (
              <ul className="space-y-2">
                <li className="flex items-start">
                  <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-green-100 text-green-800 mr-2">
                    <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </span>
                  {user?.role === 'school_admin' ? 'Unlimited users' : 'Basic access'}
                </li>
                <li className="flex items-start">
                  <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-green-100 text-green-800 mr-2">
                    <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </span>
                  Standard support
                </li>
              </ul>
            )}
            
            <div className="mt-6 p-4 bg-blue-50 text-blue-800 rounded-md">
              <h4 className="font-semibold mb-2">Need Help?</h4>
              <p className="text-sm">
                If you have any questions about your subscription or need assistance, 
                please contact our support team.
              </p>
              <a 
                href="mailto:support@meira.co.ke"
                className="text-sm text-primary font-medium hover:underline mt-2 inline-block"
              >
                support@meira.co.ke
              </a>
            </div>
          </div>
        </div>
      </div>
      
      {/* Cancel Subscription Modal */}
      <Modal 
        isOpen={showCancelModal} 
        onClose={() => setShowCancelModal(false)}
        title="Cancel Subscription"
      >
        <div className="p-6">
          <p className="mb-4">
            Are you sure you want to cancel your subscription? You will continue to have access 
            until the end of your current billing period ({formatDate(subscription.endDate)}).
          </p>
          <div className="flex justify-end space-x-3">
            <button
              onClick={() => setShowCancelModal(false)}
              className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Keep Subscription
            </button>
            <button
              onClick={handleCancelConfirm}
              className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 disabled:opacity-50"
              disabled={cancelling}
            >
              {cancelling ? (
                <span className="flex items-center">
                  <FaSpinner className="animate-spin mr-2" /> Cancelling...
                </span>
              ) : 'Confirm Cancellation'}
            </button>
          </div>
        </div>
      </Modal>
      
      {/* Change Plan Modal */}
      <Modal 
        isOpen={showChangeModal} 
        onClose={() => !changingPlan && setShowChangeModal(false)}
        title="Change Subscription Plan"
      >
        <div className="p-6">
          {changeSuccess ? (
            <div className="text-center py-6">
              <div className="mx-auto w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <FaCheck className="text-green-500 text-xl" />
              </div>
              <h3 className="text-xl font-medium mb-2">Plan Change Successful</h3>
              <p className="text-gray-600 mb-4">
                Your subscription has been updated successfully. 
                {needsCheckout && " Proceeding to checkout..."}
              </p>
            </div>
          ) : (
            <>
              {changeError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-md">
                  {changeError}
                </div>
              )}
              
              <div className="mb-4">
                <label className="block text-gray-700 mb-2">Select Plan</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {Object.entries(subscriptionPlans)
                    .filter(([key]) => key !== 'free' || key === getCurrentTier())
                    .map(([key, plan]) => (
                      <div
                        key={key}
                        className={`border rounded-md p-3 cursor-pointer transition-colors ${
                          selectedTier === key 
                            ? 'border-primary bg-primary bg-opacity-5' 
                            : 'border-gray-200 hover:border-primary'
                        }`}
                        onClick={() => setSelectedTier(key)}
                      >
                        <div className="font-medium">{plan.name}</div>
                        <div className="text-sm text-gray-500">{plan.description}</div>
                      </div>
                    ))}
                </div>
              </div>
              
              <div className="mb-6">
                <label className="block text-gray-700 mb-2">Billing Cycle</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div
                    className={`border rounded-md p-3 cursor-pointer transition-colors ${
                      billingCycle === 'quarterly' 
                        ? 'border-primary bg-primary bg-opacity-5' 
                        : 'border-gray-200 hover:border-primary'
                    }`}
                    onClick={() => setBillingCycle('quarterly')}
                  >
                    <div className="font-medium">Quarterly</div>
                    <div className="text-sm text-gray-500">Billed every 3 months</div>
                  </div>
                  <div
                    className={`border rounded-md p-3 cursor-pointer transition-colors ${
                      billingCycle === 'annual' 
                        ? 'border-primary bg-primary bg-opacity-5' 
                        : 'border-gray-200 hover:border-primary'
                    }`}
                    onClick={() => setBillingCycle('annual')}
                  >
                    <div className="font-medium">Annual</div>
                    <div className="text-sm text-gray-500">Save up to 20% vs quarterly</div>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowChangeModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                  disabled={changingPlan}
                >
                  Cancel
                </button>
                <button
                  onClick={handleChangeTier}
                  className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark disabled:opacity-50"
                  disabled={!selectedTier || !billingCycle || changingPlan}
                >
                  {changingPlan ? (
                    <span className="flex items-center">
                      <FaSpinner className="animate-spin mr-2" /> Processing...
                    </span>
                  ) : 'Confirm Change'}
                </button>
              </div>
            </>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default SubscriptionDetails;